import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { acceptTerms, getLoggedInExternalUser } from "../api/ExternalUsers";
import { Roles, useAuth } from "../providers/AuthContext";
import TermsAcceptanceModal from "./TermsAcceptanceModal";
import { useTranslation } from "react-i18next";

const TermsAcceptanceGate = ({ children }) => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole, isAuthenticated, logout, rolesLoading, user } = useAuth();
    const [showTerms, setShowTerms] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const checkedAccountRef = useRef(null);

    useEffect(() => {
        if (!isAuthenticated || rolesLoading || !user) {
            checkedAccountRef.current = null;
            setShowTerms(false);
            return;
        }

        if (hasRole(Roles.Admin)) {
            setShowTerms(false);
            return;
        }

        const accountId = user.homeAccountId || user.localAccountId || user.username;
        if (checkedAccountRef.current === accountId) {
            return;
        }

        let cancelled = false;

        const checkTerms = async () => {
            try {
                const token = await getAccessToken();
                const externalUser = await getLoggedInExternalUser(token);
                const termsAccepted = externalUser?.termsAccepted;

                if (!cancelled) {
                    checkedAccountRef.current = accountId;
                    setShowTerms(termsAccepted === false);
                }
            } catch (error) {
                console.error("Failed to check external user terms:", error);
            }
        };

        checkTerms();

        return () => {
            cancelled = true;
        };
    }, [getAccessToken, hasRole, isAuthenticated, rolesLoading, user]);

    const handleAccept = async () => {
        const toastId = toast.loading(t("toast.acceptingTerms"));
        setIsAccepting(true);

        try {
            const token = await getAccessToken();
            await acceptTerms(token);
            setShowTerms(false);
            toast.success(t("toast.termsAccepted"), { id: toastId });
        } catch (error) {
            console.error("Failed to accept terms:", error);
            toast.error(error?.message || t("toast.failedAcceptTerms"), { id: toastId });
        } finally {
            setIsAccepting(false);
        }
    };

    return (
        <>
            {children}
            <TermsAcceptanceModal
                isOpen={showTerms}
                isAccepting={isAccepting}
                onAccept={handleAccept}
                onLogout={logout}
            />
        </>
    );
};

export default TermsAcceptanceGate;
